import axios, { AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getUnauthenticatedRedirectUrl } from '../../auth/unauthenticated-redirect';

/** Credential-based auth routes: never refresh or attach a stale Bearer from localStorage. */
const SKIP_TOKEN_HANDLING_PATHS = new Set(['login', 'signup', 'forgot-password']);

function isCredentialAuthRequest(config: InternalAxiosRequestConfig): boolean {
  const path = (config.url ?? '').split('?')[0].replace(/^\/+/, '').toLowerCase();
  return SKIP_TOKEN_HANDLING_PATHS.has(path);
}

let isRefreshing = false;
let isRedirectingToLogin = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function parseStoredAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function isAccessTokenExpired(token: string): boolean {
  try {
    const decoded: { exp?: number } = jwtDecode(token);
    if (!decoded.exp) {
      return false;
    }
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

/** Only clear session when the stored JWT is missing or expired — avoids 401 cascades from transient failures. */
function get401ResponseMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return '';
  }
  const body = error.response?.data as { message?: string } | undefined;
  return typeof body?.message === 'string' ? body.message : '';
}

function shouldForceLogoutOn401(error: unknown): boolean {
  const token = parseStoredAccessToken();
  if (!token) {
    return true;
  }
  if (isAccessTokenExpired(token)) {
    return true;
  }

  const message = get401ResponseMessage(error).toLowerCase();
  if (!message) {
    return false;
  }

  return (
    message.includes('trial account expired') ||
    message.includes('account not found') ||
    message.includes('invalid token') ||
    message.includes('disabled') ||
    message.includes('not authorised')
  );
}

function stripInvalidAuthorizationHeaders(config: InternalAxiosRequestConfig): void {
  const { headers } = config;
  if (!headers) {
    return;
  }
  let authValue = '';
  if (typeof headers.Authorization === 'string') {
    authValue = headers.Authorization;
  } else if (typeof headers.authorization === 'string') {
    authValue = headers.authorization;
  }
  if (
    !authValue ||
    authValue === 'Bearer null' ||
    authValue === 'Bearer undefined' ||
    authValue === 'Bearer '
  ) {
    delete headers.Authorization;
    delete headers.authorization;
  }
}

function attachAccessToken(request: InternalAxiosRequestConfig, token: string): void {
  const headers = AxiosHeaders.from(request.headers ?? {});
  headers.set('Authorization', `Bearer ${token}`);
  request.headers = headers;
}

const refreshJWTtoken = (jwtToken: string) => {
  const token: { iat: number } = jwtDecode(jwtToken);
  const issueDate = token.iat !== undefined && new Date(token.iat * 1000);
  const currentDate = new Date();
  if (typeof issueDate !== 'boolean') {
    const timeDifference = currentDate.getTime() - issueDate.getTime();
    const seconds = timeDifference / 1000;
    const minutes = seconds / 60;
    return minutes;
  }
  return 0;
};

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.map((callback) => callback(token));
};

const redirectToLoginOnce = () => {
  if (isRedirectingToLogin || window.location.pathname === '/login') {
    return;
  }
  isRedirectingToLogin = true;
  localStorage.clear();
  window.location.assign(getUnauthenticatedRedirectUrl());
};

const API = (baseUrl: string | undefined): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
  });

  instance.interceptors.request.use(async (request) => {
    if (isCredentialAuthRequest(request)) {
      delete request.headers?.Authorization;
      delete request.headers?.authorization;
      return request;
    }

    stripInvalidAuthorizationHeaders(request);

    const parsedToken = parseStoredAccessToken();

    if (parsedToken) {
      const minutesSinceIssue = refreshJWTtoken(parsedToken);

      if (minutesSinceIssue > 15) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const response = await fetch(`${process.env.REACT_APP_USER_SERVER_URL}/refresh`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${parsedToken}`,
              },
            });
            if (response.ok) {
              const data = await response.json();
              const refreshedToken = data.data?.token as string;
              localStorage.setItem('token', JSON.stringify(refreshedToken));
              attachAccessToken(request, refreshedToken);
              isRefreshing = false;
              onRefreshed(refreshedToken);
              refreshSubscribers = [];
            } else {
              refreshSubscribers = [];
              redirectToLoginOnce();
              isRefreshing = false;
              throw new Error('Token refresh failed');
            }
          } catch (error) {
            refreshSubscribers = [];
            redirectToLoginOnce();
            isRefreshing = false;
            throw error;
          }
        }
        const retryOriginalRequest = new Promise<string>((resolve) => {
          subscribeTokenRefresh((value: string) => {
            attachAccessToken(request, value);
            resolve(value);
          });
        });
        return retryOriginalRequest.then((tokenVal) => {
          attachAccessToken(request, tokenVal);
          return request;
        });
      }
      attachAccessToken(request, parsedToken);
    }

    return request;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && shouldForceLogoutOn401(error)) {
        redirectToLoginOnce();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export default API;
